import React, { useEffect, useState } from 'react';
import { Box, Typography, Link, IconButton, CircularProgress } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useAssetsApi } from 'src/modules/assetLibrary/api/AssetsApi';

interface VcfContact {
  fullName?: string;
  phone?: string;
  email?: string;
  url?: string;
  photo?: string;
}

// vCard folds long lines by continuing them with a leading space/tab (RFC 6350)
const unfoldVcfLines = (vcfText: string): string[] => {
  const lines: string[] = [];
  vcfText.split(/\r?\n/).forEach(line => {
    if ((line.startsWith(' ') || line.startsWith('\t')) && lines.length > 0) {
      lines[lines.length - 1] += line.slice(1);
    } else {
      lines.push(line);
    }
  });
  return lines;
};

// Supports inline base64 (v2.1/3.0 ENCODING=b/BASE64), data URIs and http(s) URLs (v4.0)
const parsePhoto = (line: string): string | undefined => {
  const colonIndex = line.indexOf(':');
  if (colonIndex === -1) return undefined;
  const params = line.substring(0, colonIndex).toUpperCase();
  const value = line.substring(colonIndex + 1).trim();
  if (!value) return undefined;
  if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:')) {
    return value;
  }
  if (params.includes('ENCODING=B') || params.includes('BASE64')) {
    const typeMatch = params.match(/(?:^|;)TYPE=([A-Z0-9-]+)/);
    const imageType = typeMatch ? typeMatch[1].toLowerCase() : 'jpeg';
    return `data:image/${imageType};base64,${value.replace(/\s/g, '')}`;
  }
  return undefined;
};

const parseVcf = (vcfText: string) => {
  const lines = unfoldVcfLines(vcfText);
  const contact: VcfContact = {};
  lines.forEach(line => {
    if (line.startsWith('FN:')) {
      contact.fullName = line.substring(3).trim();
    } else if (line.startsWith('TEL')) {
      const telMatch = line.match(/TEL.*:(.*)/);
      if (telMatch) contact.phone = telMatch[1].trim();
    } else if (line.startsWith('EMAIL')) {
      const emailMatch = line.match(/EMAIL.*:(.*)/);
      if (emailMatch) contact.email = emailMatch[1].trim();
    } else if (line.startsWith('URL')) {
      const urlMatch = line.match(/URL.*:(.*)/);
      if (urlMatch) contact.url = urlMatch[1].trim();
    } else if (line.startsWith('PHOTO')) {
      const photo = parsePhoto(line);
      if (photo) contact.photo = photo;
    }
  });
  return contact;
};

const ContactPhoto = ({ photo, fullName }: { photo: string; fullName?: string }) => (
  <Box
    component="img"
    src={photo}
    alt={fullName || 'Contact photo'}
    sx={{
      width: 64,
      height: 64,
      borderRadius: '50%',
      objectFit: 'cover',
      marginBottom: 1,
    }}
  />
);

const VcfDisplayFromUrl = ({ vcfUrl, fontSize = '14px' }: { vcfUrl: string, fontSize?: string }) => {
  const { getFileContent } = useAssetsApi();
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState<VcfContact>();

  useEffect(() => {
    if (!vcfUrl) return;
    getFileContent(vcfUrl).then(async (response: {data: Blob}) => {
      const text = await response.data.text();
      const parsed = parseVcf(text);
      setContact(parsed);
    }).finally(() => {
      setLoading(false);
    });
  }, [vcfUrl])

  if (loading) return <Box minHeight={'130px'} display="flex" justifyContent="center" alignItems="center"><CircularProgress /></Box>;

  if (!contact) return <Box minHeight={'130px'}></Box>;

  return (
    <Box p={2} border={1} borderRadius={2} marginTop={'20px'}>
      {contact.photo && <ContactPhoto photo={contact.photo} fullName={contact.fullName} />}
      <Typography fontSize={fontSize} variant="h6">{contact.fullName || 'No Name Found'}</Typography>
      {contact.phone && <Typography fontSize={fontSize}>Phone: {contact.phone}</Typography>}
      {contact.email && <Typography fontSize={fontSize}>Email: {contact.email}</Typography>}
      {contact.url && (
        <Typography fontSize={fontSize}>
          Website:{' '}
          <Link href={contact.url} target="_blank" rel="noopener noreferrer">
            {contact.url}
          </Link>
        </Typography>
      )}
    </Box>
  );
};

const VcfDisplayFromText = ({ vcfText, fontSize = '14px', onDelete }: { vcfText: string, fontSize?: string, onDelete?: () => void }) => {
  const [contact, setContact] = useState<VcfContact>();

  useEffect(() => {
    if (!vcfText) return;
    const parsed = parseVcf(vcfText);
    setContact(parsed);
  }, [vcfText]);

  if (!contact) return <Box minHeight={'130px'}></Box>;

  return (
    <Box p={2} border={1} borderRadius={2} marginTop={'20px'} position="relative">
      {onDelete && (
        <IconButton
          onClick={onDelete}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
      {contact.photo && <ContactPhoto photo={contact.photo} fullName={contact.fullName} />}
      <Typography fontSize={fontSize} variant="h6">{contact.fullName || 'No Name Found'}</Typography>
      {contact.phone && <Typography fontSize={fontSize}>Phone: {contact.phone}</Typography>}
      {contact.email && <Typography fontSize={fontSize}>Email: {contact.email}</Typography>}
      {contact.url && (
        <Typography fontSize={fontSize}>
          Website:{' '}
          <Link href={contact.url} target="_blank" rel="noopener noreferrer">
            {contact.url}
          </Link>
        </Typography>
      )}
    </Box>
  );
};

export default VcfDisplayFromUrl;
export { VcfDisplayFromText };
